import React, { Fragment, useEffect, useState } from 'react';
import './App.scss';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { useCookie } from 'react-use';
import { ascend, compose, prop, sort, is } from 'ramda';
import jwtDecode from 'jwt-decode';
import { UserResource } from '~~apis/resource';
import ApiService from '~~commonAPIs/ApiService';
import CoverLoading from '~~elements/Loading/Cover';
import FormalNavBar from './common/components/FormalNavBar';
import Footer from './common/components/Footer';
import LoginRequired from './features/LoginRequired';
import NotFound from './features/common/NotFound';
import Login from './features/login';
import Logout from './features/Logout';
import Privacy from './features/privacy';
import Settings from './features/Settings';
import PluginShop from './features/PluginShop';
import Tournament from './features/Tournament';
import CreateTournament from './features/CreateTournament';
import UserTournamentRegistry from './features/UserTournamentRegistry';
import MyPluginOrder from './features/MyPluginOrder';
import MemberManagement from './features/MemberManagement';
import { ROOT_ACTION } from './actionTypes';
import { RootProvider, useRootDispatch, useRootState } from './useRoot';

const loginRouters = [
  {
    path: '/settings/:section',
    component: Settings,
    exact: true,
  },
  {
    path: '/organizer/:organizer_uniqid/create_tournament/:section',
    component: CreateTournament,
    exact: true,
  },
  {
    path: '/organizer/:organizer_uniqid/tournament/:tournament_uniqid/:section',
    component: Tournament,
    exact: true,
  },
  {
    // /myregistries/tournament?registry=xxx&team=xxx
    path: '/myregistries/tournament',
    component: UserTournamentRegistry,
  },
  {
    // /myorders/plugin?popup=result&result=success&wait=true
    path: '/myorders/plugin',
    component: MyPluginOrder,
  },
  {
    path: '/member_management',
    component: MemberManagement,
  },
];

const routes = [
  {
    path: '/login',
    component: Login,
    exact: true,
  },
  {
    path: '/logout',
    component: Logout,
    exact: true,
  },
  {
    path: '/privacy',
    component: Privacy,
    exact: true,
  },
  {
    path: '/shop/plugin',
    component: PluginShop,
  },
  {
    path: '*',
    component: NotFound,
    status: 404,
  },
];

function App() {
  return (
    <RootProvider>
      <div className="App">
        <RootContainer />
      </div>
    </RootProvider>
  );
}

function RootContainer() {
  const { loading, loginInfo, isAdmin } = useRootState();
  const rootDispatch = useRootDispatch();
  const [accessToken] = useCookie('RB_UID');
  const [refreshToken] = useCookie('RB_RID');

  useEffect(() => {
    const recaptchaKey = document.querySelector('meta[name="google-recaptcha-score-key"]').getAttribute('content');
    if (recaptchaKey !== '' && recaptchaKey.slice(0, 2) !== '{{') {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaKey}`;
      document.querySelector('head').appendChild(script);
    }
  }, []);

  useEffect(() => {
    const parseAccessToken = (token) => {
      const resolvedUserInfo = jwtDecode(token);
      rootDispatch({
        type: ROOT_ACTION.SET_LOGIN_INFO,
        payload: {
          isLogin: true,
          ...resolvedUserInfo,
        },
      });

      UserResource.permissionList()
        .then(({ data }) => {
          const nameAscend = ascend(compose(prop('name'), prop('info')));
          const sortedPerms = {
            first_bases: sort(nameAscend, data.first_bases),
            leagues: sort(nameAscend, data.leagues),
            teams: sort(nameAscend, data.teams),
            players: sort(nameAscend, data.players),
          };

          rootDispatch({
            type: ROOT_ACTION.SET_PERMISSION_LIST,
            payload: {
              ...data,
              ...sortedPerms,
            },
          });
          rootDispatch({
            type: ROOT_ACTION.SET_IS_ADMIN,
            payload: data?.platform === 'ADMIN' || data?.platform === 'EDITOR' ? data.platform : false,
          });
        })
        .catch(() => {});
    };

    if (!is(String, accessToken)) {
      rootDispatch({
        type: ROOT_ACTION.SET_LOGIN_INFO,
        payload: {
          isLogin: false,
        },
      });
      if (is(String, refreshToken) && refreshToken !== '') {
        ApiService.refreshToken()
          .then((newToken) => {
            parseAccessToken(newToken);
          })
          .catch(() => {});
      }
    } else {
      parseAccessToken(accessToken);
    }
  }, [accessToken, refreshToken, rootDispatch]);

  return (
    <Fragment>
      <FormalNavBar userInfo={{ ...loginInfo, isAdmin }} />
      <div id="app-content">
        <Router>
          <Switch>
            {loginRouters.map((route, i) => (
              <LoginRouteWithSubRoutes key={`login-rout-${i}`} {...route} />
            ))}
            {routes.map((route, i) => (
              <RouteWithSubRoutes key={`route-${i}`} {...route} />
            ))}
          </Switch>
        </Router>
      </div>
      <Footer />
      {loading && <CoverLoading />}
    </Fragment>
  );
}

export default App;

function RouteWithSubRoutes(route) {
  return (
    <Route
      path={route.path}
      render={(props) => <route.component {...props} {...(!route.props ? {} : route.props)} routes={route.routes} />}
    />
  );
}

function LoginRouteWithSubRoutes(route) {
  return (
    <Route
      path={route.path}
      render={(props) => (
        <LoginRequired>
          <route.component {...props} {...(!route.props ? {} : route.props)} routes={route.routes} />
        </LoginRequired>
      )}
    />
  );
}
